import { useState } from "react";
import { Bluetooth, BluetoothOff, ExternalLink, Loader2, Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cardCls, blueBtn } from "@/components/mobile/parts";
import { BrowserPrinterCard } from "@/components/mobile/config/BrowserPrinterCard";
import { useAuth } from "@/context/AuthContext";
import { RAWBT_STORE, btError, btSupported, btTest, getBt, rawbtSupported, saveBt } from "@/lib/btprint";
import { cn } from "@/lib/utils";

const MODES = [["browser", "Bluetooth do navegador", "Só impressoras Bluetooth LE"], ["rawbt", "App RawBT", "Qualquer impressora pareada no celular"]];

function ModePicker({ mode, can, onMode }) {
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="m-bt-mode">
      {MODES.map(([k, l, d]) => (
        <button key={k} type="button" disabled={!can[k]} onClick={() => onMode(k)} data-testid={`m-bt-mode-${k}`}
          className={cn("rounded-xl border p-3 text-left transition-colors disabled:opacity-40", mode === k ? "border-[#1565c0] bg-[#e3f2fd]" : "bg-white")}>
          <span className="block text-sm font-semibold">{l}</span><span className="block text-xs text-slate-500">{d}</span>
        </button>
      ))}
    </div>
  );
}

function RawbtCard({ p, store }) {
  const [busy, setBusy] = useState(false);
  const test = async () => { setBusy(true); try { await btTest(p, store); toast.success("Teste enviado para o RawBT"); } catch (e) { toast.error(btError(e)); } setBusy(false); };
  return (
    <div className={cn(cardCls, "space-y-3 p-4")} data-testid="m-rawbt-card">
      <ol className="list-decimal space-y-1.5 pl-5 text-sm">
        <li>Instale o app RawBT: <a href={RAWBT_STORE} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-[#1565c0] underline" data-testid="m-rawbt-store-link">abrir na Play Store<ExternalLink className="h-3.5 w-3.5" /></a></li>
        <li>Pareie a impressora nas configurações de Bluetooth do celular.</li>
        <li>Abra o RawBT e escolha a impressora na lista de pareadas.</li>
        <li>Volte aqui e toque em Imprimir teste.</li>
      </ol>
      <Button className={cn("h-12 w-full", blueBtn)} onClick={test} disabled={busy} data-testid="m-rawbt-test-btn">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />}Imprimir teste</Button>
    </div>
  );
}

function Columns({ p, update }) {
  return (
    <label className={cn(cardCls, "block space-y-1 p-4 text-sm")}>
      <span className="font-semibold">Largura do papel</span>
      <select value={p.columns} onChange={(e) => update({ columns: Number(e.target.value) })} className="h-11 w-full rounded-md border border-input bg-white px-3" data-testid="m-bt-columns-select">
        <option value={32}>58 mm (32 colunas)</option>
        <option value={48}>80 mm (48 colunas)</option>
      </select>
    </label>
  );
}

export function ImpressaoTab() {
  const { session } = useAuth();
  const uid = session.user.id;
  const [p, setP] = useState(() => getBt(uid));
  const update = (patch) => setP((x) => { const n = { ...x, ...patch }; saveBt(uid, n); return n; });
  const can = { browser: btSupported(), rawbt: rawbtSupported() };
  const ok = can.browser || can.rawbt;
  const mode = p.mode === "rawbt" && can.rawbt ? "rawbt" : can.browser ? "browser" : "rawbt";
  const store = session.tenant.nome_fantasia;
  return (
    <div className="space-y-4" data-testid="m-config-impressao">
      <label className={cn(cardCls, "flex items-start gap-3 p-4", !ok && "opacity-60")} data-testid="m-bt-row">
        <Bluetooth className="mt-0.5 h-5 w-5 shrink-0 text-[#1565c0]" />
        <span className="flex-1">
          <span className="block font-semibold">Imprimir por Bluetooth</span>
          <span className="block text-xs text-slate-500">Conta, comprovantes e tickets da venda direta saem na impressora Bluetooth deste celular. Pedidos da cozinha continuam indo para o caixa.</span>
        </span>
        <Switch checked={ok && p.enabled} disabled={!ok} onCheckedChange={(v) => update({ enabled: v, mode })} data-testid="m-bt-switch" />
      </label>
      {!ok && (
        <div className="flex items-start gap-3 rounded-2xl bg-amber-50 p-4 text-sm text-amber-900 ring-1 ring-amber-200" data-testid="m-bt-unsupported">
          <BluetoothOff className="mt-0.5 h-5 w-5 shrink-0" />Este navegador não permite Bluetooth. Abra o app no Chrome do Android para usar a impressora.
        </div>
      )}
      {ok && p.enabled && (
        <>
          <ModePicker mode={mode} can={can} onMode={(m) => update({ mode: m })} />
          <p className="rounded-2xl bg-[#e3f2fd] p-4 text-sm font-medium text-[#0d47a1]" data-testid="m-bt-instruction">Pareie a impressora nas configurações de Bluetooth do seu celular primeiro, depois volte aqui para selecioná-la.</p>
          {mode === "rawbt" ? <RawbtCard p={{ ...p, mode }} store={store} /> : <BrowserPrinterCard p={p} update={update} store={store} />}
          <Columns p={p} update={update} />
        </>
      )}
      <p className="px-1 text-xs text-slate-500">{mode === "rawbt" ? "Com o RawBT, a impressão é enviada para o app, que imprime na impressora escolhida nele." : "Pelo navegador, a impressora precisa ter Bluetooth LE (BLE)."} Esta escolha fica salva só para você, neste celular.</p>
    </div>
  );
}
